import { useEffect, useState, type ReactNode } from "react";
import { Link } from "gatsby";
import styled from "styled-components";

import "@fontsource/marcellus/400.css";
import "@fontsource/karla/400.css";
import "@fontsource/karla/500.css";
import "@fontsource/karla/700.css";

import GlobalStyle from "./global-style";
import { OnDesktop, OnMobile, PillLink } from "./ui";
import { bp, colors, fonts, gutter, radius, shadow } from "../theme";
import { contact, navPages } from "../data/site";
import logoMark from "../images/logo-mark.svg";

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Topbar = styled.div`
  background: ${colors.goldLight};
  color: ${colors.ink};
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.04em;
  padding: 9px ${gutter};
  display: flex;
  justify-content: space-between;
  gap: 16px;

  a:hover {
    color: ${colors.ink};
    text-decoration: underline;
  }

  ${bp.mobile} {
    justify-content: center;
    text-align: center;
    font-size: 12px;
    padding: 8px 20px;
  }
`;

const Head = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${colors.bg};
  border-bottom: 1px solid ${colors.borderSoft};
  padding: 16px ${gutter};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  ${bp.mobile} {
    padding: 12px 20px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: none;

  img {
    width: 56px;
    height: 56px;
    object-fit: contain;
  }

  span {
    font-family: ${fonts.title};
    font-size: 24px;
    color: ${colors.ink};
    white-space: nowrap;
  }

  ${bp.mobile} {
    gap: 10px;

    img {
      width: 40px;
      height: 40px;
    }

    span {
      font-size: 16px;
      white-space: normal;
    }
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 26px;
  font-weight: 700;
  font-size: 14px;
  color: ${colors.inkSoft};

  a {
    padding-bottom: 4px;
    border-bottom: 2px solid transparent;
  }

  @media (max-width: 1150px) {
    gap: 18px;
    font-size: 13px;
  }

  ${bp.mobile} {
    display: none;
  }
`;

const HeaderCta = styled(PillLink)`
  flex: none;
  padding: 12px 24px;
  font-size: 14px;

  ${bp.mobile} {
    display: none;
  }
`;

const Burger = styled.button`
  width: 44px;
  height: 44px;
  flex: none;
  display: none;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  justify-content: center;
  background: ${colors.goldSoft};
  border: 0;
  border-radius: ${radius.sm};
  cursor: pointer;

  span {
    width: 18px;
    height: 2px;
    background: ${colors.gold};
    display: block;
  }

  ${bp.mobile} {
    display: flex;
  }
`;

const MobileNav = styled.nav`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${colors.bg};
  border-bottom: 1px solid ${colors.borderSoft};
  box-shadow: ${shadow.menu};
  display: flex;
  flex-direction: column;

  a {
    padding: 16px 24px;
    font-weight: 700;
    font-size: 16px;
    border-bottom: 1px solid ${colors.borderFaint};
  }

  a:last-child {
    border-bottom: 0;
  }

  ${bp.desktop} {
    display: none;
  }
`;

const activeDesktop = { borderBottomColor: colors.gold };
const activeMobile = { color: colors.goldText };

const Foot = styled.footer`
  background: ${colors.ink};
  color: ${colors.footerText};
  padding: 48px ${gutter};
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  gap: 40px;

  ${bp.mobile} {
    flex-direction: column;
    gap: 10px;
    padding: 28px 20px;
  }
`;

const FootBrand = styled.div`
  font-family: ${fonts.title};
  font-size: 20px;
  color: ${colors.onDark};

  div {
    font-family: ${fonts.body};
    font-size: 14px;
    line-height: 1.7;
    color: ${colors.footerMuted};
    margin-top: 10px;
  }

  ${bp.mobile} {
    font-size: 16px;

    div {
      font-size: 13px;
      margin-top: 8px;
    }
  }
`;

const FootCol = styled.div`
  font-size: 15px;
  line-height: 2;
  display: flex;
  flex-direction: column;

  ${bp.mobile} {
    font-size: 13px;
    line-height: 1.7;
    color: ${colors.footerMuted};
  }
`;

const FootNote = styled(FootCol)`
  text-align: right;
  color: ${colors.footerMuted};

  ${bp.mobile} {
    text-align: left;
  }
`;

const Main = styled.main`
  flex: 1;
`;

const Layout = ({
  children,
  location,
}: {
  children: ReactNode;
  location?: { pathname?: string };
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const pathname = location?.pathname;

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <Shell>
      <GlobalStyle />
      <Topbar>
        <OnDesktop>{contact.area}</OnDesktop>
        <OnDesktop>
          <a href={contact.phoneHref}>{contact.phone}</a> ·{" "}
          <a href={contact.emailHref}>{contact.email}</a>
        </OnDesktop>
        <OnMobile>
          {contact.areaShort} · <a href={contact.phoneHref}>{contact.phone}</a>
        </OnMobile>
      </Topbar>

      <Head>
        <Brand to="/" onClick={closeMenu}>
          <img src={logoMark} alt="Logo des écuries du cerf" />
          <span>LES ÉCURIES DU CERF</span>
        </Brand>

        <DesktopNav>
          {navPages.map((page) => (
            <Link key={page.href} to={page.href} activeStyle={activeDesktop}>
              {page.label}
            </Link>
          ))}
        </DesktopNav>

        <HeaderCta to="/contact/" $variant="soft">
          Contact
        </HeaderCta>

        <Burger
          type="button"
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={menuOpen}
          aria-controls="menu-mobile"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </Burger>

        {menuOpen && (
          <MobileNav id="menu-mobile">
            {[...navPages, { label: "Contact", href: "/contact/" }].map(
              (page) => (
                <Link
                  key={page.href}
                  to={page.href}
                  activeStyle={activeMobile}
                  onClick={closeMenu}
                >
                  {page.label}
                </Link>
              ),
            )}
          </MobileNav>
        )}
      </Head>

      <Main>{children}</Main>

      <Foot>
        <FootBrand>
          LES ÉCURIES DU CERF
          <div>
            {contact.street} — {contact.city}
          </div>
        </FootBrand>
        <FootCol>
          <a href={contact.phoneHref}>{contact.phone}</a>
          <a href={contact.emailHref}>{contact.email}</a>
        </FootCol>
        <FootNote>Visites sur rendez-vous</FootNote>
      </Foot>
    </Shell>
  );
};

export default Layout;
